import React, { useState, useEffect } from "react"
import * as api from "../api"
import * as v2layouts from "../layouts"
import * as layouts from "layouts"
import * as sessions from "sessions"
import * as authzc from "authzcached"
import * as media from "media"
import * as icons from "icons"
import { SuggestionPanel } from "./suggestion"
import { ConfigurationModal } from "./configuration"

interface AssetCardProps {
	asset: api.AssetExtract
	isSelected: boolean
	onSelect: () => void
	onApprove: () => Promise<void>
	onReject: () => Promise<void>
	onConfigure: (config: api.AssetConfigureRequest) => Promise<void>
}

export function AssetCard({
	asset,
	isSelected,
	onSelect,
	onApprove,
	onReject,
	onConfigure,
}: AssetCardProps): JSX.Element {
	const [showConfigModal, setShowConfigModal] = useState(false)
	const [showDetailView, setShowDetailView] = useState(false)

	const handleApprove = async (): Promise<void> => {
		await onApprove()
		setShowConfigModal(true)
	}

	return (
		<>
			<div style={{ position: "relative" }}>
				<v2layouts.Card onClick={() => setShowDetailView(true)}>
					<div
						style={{
							position: "absolute",
							top: "10px",
							left: "10px",
							zIndex: 10,
						}}
						onClick={(e) => e.stopPropagation()}
					>
						<v2layouts.Checkbox checked={isSelected} onChange={onSelect} />
					</div>

					<AssetPreview asset={asset} />

					<v2layouts.CardContent>
						<v2layouts.CardTitle>
							{asset.asset_type} {asset.asset_subtype && `- ${asset.asset_subtype}`}
						</v2layouts.CardTitle>

						{asset.page_number !== undefined && (
							<v2layouts.CardMeta>Page {asset.page_number}</v2layouts.CardMeta>
						)}

						{asset.extracted_context && (
							<v2layouts.CardMeta style={{ fontSize: "11px", marginTop: "4px", fontStyle: "italic" }}>
								"{asset.extracted_context.substring(0, 60)}..."
							</v2layouts.CardMeta>
						)}

						{asset.configured_as && (
							<v2layouts.Badge
								$status="approved"
								style={{ marginTop: "8px", cursor: "pointer" }}
								onClick={() => setShowConfigModal(true)}
							>
								Configured as: {asset.configured_as}
							</v2layouts.Badge>
						)}
					</v2layouts.CardContent>

					<div onClick={(e) => e.stopPropagation()}>
						<SuggestionPanel
							asset={asset}
							onApprove={handleApprove}
							onReject={onReject}
							onConfigure={() => setShowConfigModal(true)}
						/>
					</div>
				</v2layouts.Card>
			</div>

			{showConfigModal && (
				<ConfigurationModal asset={asset} onClose={() => setShowConfigModal(false)} onSave={onConfigure} />
			)}

			{showDetailView && (
				<AssetDetailView
					asset={asset}
					onClose={() => setShowDetailView(false)}
					onConfigure={() => {
						setShowDetailView(false)
						setShowConfigModal(true)
					}}
					onApprove={handleApprove}
					onReject={onReject}
				/>
			)}
		</>
	)
}

function AssetPreview({ asset }: { asset: api.AssetExtract }): JSX.Element {
	const [imageUrl, setImageUrl] = useState<string | null>(null)
	const [loading, setLoading] = useState(false)
	const account = sessions.useAccount()
	const bearertoken = sessions.useToken()

	useEffect(() => {
		if (asset.asset_type === "image" && asset.content_md5) {
			setLoading(true)
			media.cache
				.current(account.id, asset.content_md5, bearertoken)
				.then((url) => setImageUrl(url))
				.catch((cause) => console.error("unable to load image", cause))
				.finally(() => setLoading(false))
		}
	}, [asset.content_md5, asset.asset_type])

	if (asset.asset_type === "image" && asset.content_md5) {
		if (loading) {
			return (
				<div style={{ minHeight: "200px", display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f5f5" }}>
					<layouts.loading.pending loading={true} />
				</div>
			)
		}
		if (imageUrl) {
			return <v2layouts.CardImage src={imageUrl} />
		}
		return (
			<div style={{ minHeight: "200px", display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f5f5" }}>
				Failed to load image
			</div>
		)
	}

	if (asset.asset_type === "text" && asset.content_text) {
		const cleanText = asset.content_text.replace(/!\[.*?\]\(.*?\)/g, "").trim()

		return (
			<div
				style={{
					padding: "20px",
					background: "#f5f5f5",
					minHeight: "150px",
					maxHeight: "200px",
					overflow: "hidden",
					fontSize: "13px",
					lineHeight: "1.5",
					color: "#424242",
					whiteSpace: "pre-wrap",
				}}
			>
				{cleanText.substring(0, 300)}
				{cleanText.length > 300 && "..."}
			</div>
		)
	}

	return (
		<div
			style={{
				padding: "20px",
				background: "#f5f5f5",
				minHeight: "150px",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				color: "#9e9e9e",
			}}
		>
			No preview available
		</div>
	)
}

interface AssetDetailViewProps {
	asset: api.AssetExtract
	onClose: () => void
	onConfigure: () => void
	onApprove: () => Promise<void>
	onReject: () => Promise<void>
}

function AssetDetailView({ asset, onClose, onConfigure, onApprove, onReject }: AssetDetailViewProps): JSX.Element {
	const [imageUrl, setImageUrl] = useState<string | null>(null)
	const [loading, setLoading] = useState(false)
	const [actionInProgress, setActionInProgress] = useState(false)
	const account = sessions.useAccount()
	const bearertoken = sessions.useToken()

	const handleApprove = async () => {
		setActionInProgress(true)
		try {
			await onApprove()
			onClose()
		} catch (err) {
			console.error("approve failed", err)
			setActionInProgress(false)
		}
	}

	const handleReject = async () => {
		setActionInProgress(true)
		try {
			await onReject()
			onClose()
		} catch (err) {
			console.error("reject failed", err)
			setActionInProgress(false)
		}
	}

	useEffect(() => {
		if (asset.asset_type === "image" && asset.content_md5) {
			setLoading(true)
			media.cache
				.current(account.id, asset.content_md5, bearertoken)
				.then((url) => setImageUrl(url))
				.catch((cause) => console.error("unable to load image", cause))
				.finally(() => setLoading(false))
		}
	}, [asset.content_md5, asset.asset_type])

	return (
		<v2layouts.Modal onClick={onClose}>
			<div
				style={{
					position: "relative",
					width: "90vw",
					height: "90vh",
					background: "white",
					borderRadius: "10px",
					display: "flex",
					flexDirection: "column",
				}}
				onClick={(e) => e.stopPropagation()}
			>
				{/* Close button */}
				<div
					style={{
						position: "absolute",
						top: "20px",
						right: "20px",
						zIndex: 10,
						cursor: "pointer",
						background: "white",
						borderRadius: "50%",
						padding: "8px",
						boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
					}}
					onClick={onClose}
				>
					<icons.Close width="24px" height="24px" />
				</div>

				{/* Main content area */}
				<div
					style={{
						flex: 1,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						padding: "40px",
						background: "#f5f5f5",
						borderRadius: "10px 10px 0 0",
						overflow: "auto",
					}}
				>
					{asset.asset_type === "image" && asset.content_md5 ? (
						loading ? (
							<layouts.loading.pending loading={true} />
						) : imageUrl ? (
							<img src={imageUrl} style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }} />
						) : (
							<div style={{ color: "#9e9e9e" }}>Failed to load image</div>
						)
					) : asset.asset_type === "text" && asset.content_text ? (
						<div
							style={{
								background: "white",
								padding: "30px",
								borderRadius: "8px",
								maxWidth: "800px",
								width: "100%",
								maxHeight: "100%",
								overflow: "auto",
								fontSize: "14px",
								lineHeight: "1.6",
								color: "#212121",
								whiteSpace: "pre-wrap",
							}}
						>
							{asset.content_text.replace(/!\[.*?\]\(.*?\)/g, "")}
						</div>
					) : (
						<div style={{ color: "#9e9e9e" }}>No preview available</div>
					)}
				</div>

				{/* Bottom action bar */}
				<div
					style={{
						padding: "20px 40px",
						background: "white",
						borderTop: "1px solid #e0e0e0",
						borderRadius: "0 0 10px 10px",
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<div>
						<div style={{ fontSize: "16px", fontWeight: 600, color: "#212121", marginBottom: "4px" }}>
							{asset.asset_type} {asset.asset_subtype && `- ${asset.asset_subtype}`}
						</div>
						{asset.page_number !== undefined && (
							<div style={{ fontSize: "13px", color: "#757575" }}>Page {asset.page_number}</div>
						)}
						{asset.configured_as && (
							<v2layouts.Badge $status="approved" style={{ marginTop: "8px" }}>
								Configured as: {asset.configured_as}
							</v2layouts.Badge>
						)}
					</div>

					<div style={{ display: "flex", gap: "10px" }}>
						{asset.review_status === "pending" ? (
							<>
								<v2layouts.ActionButton onClick={handleReject} disabled={actionInProgress}>
									{actionInProgress ? "Rejecting..." : "Reject"}
								</v2layouts.ActionButton>
								<v2layouts.ActionButton $variant="primary" onClick={handleApprove} disabled={actionInProgress}>
									{actionInProgress ? "Approving..." : "Approve"}
								</v2layouts.ActionButton>
							</>
						) : asset.review_status === "approved" ? (
							<v2layouts.ActionButton $variant="primary" onClick={onConfigure} disabled={actionInProgress}>
								{asset.configured_as ? "Edit Configuration" : "Configure Now"}
							</v2layouts.ActionButton>
						) : null}
					</div>
				</div>
			</div>
		</v2layouts.Modal>
	)
}
